import {Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {FuseNavigationService} from '@fuse/components/navigation/navigation.service';
import {FuseNavigation} from '@fuse/types';
import {adminFeature} from 'app/constants/role-settings/admin';
import {customerFeature} from 'app/constants/role-settings/customer';
import {userFeature} from 'app/constants/role-settings/user';
import {defaultFeatureVisible} from 'app/constants/role-settings/defaults';
import {adminNavigation} from 'app/navigation/admin-navigation';
import {customerNavigation} from 'app/navigation/customer-navigation';
import {navigation} from 'app/navigation/navigation';
import {SERVER_API_URL} from 'app/app.constants';
import {IRole, Role} from 'app/blocks/model/role.model';
import {IAbacCatalogue} from 'app/blocks/model/abac-rule.model';
import {Resource} from 'app/constants/resource';
import {upperCase} from 'lodash';
import EntityCrudService from 'app/blocks/service/api/entity-service';
import {Store} from '@ngxs/store';
import {BehaviorSubject, map, Observable} from 'rxjs';
import {Principal} from 'app/core/auth/principal.service';
import {IUserEntity} from 'app/blocks/model/user.model';

@Injectable({
    providedIn: 'root'
})
export class RoleService extends EntityCrudService<IRole> {
    roleSettings = new BehaviorSubject<any>({});
    constructor(
        http: HttpClient,
        private _fuseNavigationService: FuseNavigationService,
        private _principal: Principal
    ) {
        super(http, Resource.ROLES, (obj?) => new Role(obj));
    }

    getRole = (id: number): Promise<any> => {
        return this.http.get<any>(SERVER_API_URL + `api/roles/${id}`).toPromise();
    };

    getRoleByName = (roleName: string): Promise<any> => {
        return this.http.get<any>(SERVER_API_URL + `api/roles/name/${roleName}`).toPromise();
    };

    getRoleByAuthority = (authorityName: string): Promise<any> => {
        return this.http.get<any>(SERVER_API_URL + `api/roles/authority/${authorityName}`).toPromise();
    };

    getRoleDefaults = (authorityName: string): Promise<any> => {
        return this.http.get<any>(SERVER_API_URL + `api/roles/role-defaults/authority/${authorityName}`).toPromise();
    };

    deleteRole = (id: number): Promise<any> => {
        return this.http.delete<any>(SERVER_API_URL + `api/roles/${id}`).toPromise();
    };

    saveRole = (role: any): Promise<any> => {
        return this.http.post<any>(SERVER_API_URL + 'api/roles', role).toPromise();
    };

    getTenantMenu = (): Promise<any> => {
        return this.http.get<any>(SERVER_API_URL + 'api/roles/settings').toPromise();
    };

    getTenantProfile = (): Promise<any> => {
        return this.http.get<any>(SERVER_API_URL + 'api/roles/profiles').toPromise();
    };

    /**
     * The attribute paths a rule may use, and the operators valid for each.
     *
     * Fetched rather than hardcoded because an uncatalogued path now resolves to DENY_ALL on the server — a
     * typo silently blanks that role's users rather than leaking data, so the form must offer the real list.
     */
    getAbacCatalogue = (): Promise<IAbacCatalogue> => {
        return this.http.get<IAbacCatalogue>(SERVER_API_URL + 'api/roles/abac-catalogue').toPromise();
    };

    getMenuByAuthority = (authority: string, filters: any): FuseNavigation[] => {
        switch (authority) {
            case 'ROLE_ADMIN':
                return this.addVisibility(adminNavigation.concat(adminFeature), filters);
            case 'ROLE_CUSTOMER':
                return this.addVisibility(customerNavigation.concat(customerFeature), filters);
            default:
                return this.addVisibility(navigation.concat(userFeature), filters);
        }
    };

    addVisibility = (nav: FuseNavigation[], filters: any) => {
        const menuList = [];
        for (const item of nav) {
            const menu = {
                id: item.id,
                visible: filters != null && filters[item.id] != null ? filters[item.id] : defaultFeatureVisible(item.id),
                type: upperCase(item.type),
                children: item.children ? this.addVisibility(item.children, filters) : null,
                translationKey: item.translate
            };
            menuList.push(menu);
        }
        return menuList;
    };

    updateNavigation = (): void => {
        this.getTenantMenu().then((filters) => {
            if (filters) {
                Object.getOwnPropertyNames(filters).forEach((key: any) => {
                    this._fuseNavigationService.updateNavigationItem(key, {
                        hidden: !filters[key]
                    });
                });
                this.roleSettings.next(filters);
            }
        });
    };

    subscribeToShowSetting = (key): Observable<any> => {
        return this.roleSettings.pipe(
            map((settings) => {
                if (settings) {
                    if (settings[key + '-show'] != null) {
                        return settings[key + '-show'];
                    }
                    return true;
                }
                return true;
            })
        );
    };

    subscribeToEnableSetting = (key): Observable<any> => {
        return this.roleSettings.pipe(
            map((settings) => {
                if (settings) {
                    if (settings[key + '-enable'] != null) {
                        return settings[key + '-enable'];
                    }
                    return true;
                }
                return true;
            })
        );
    };

    showSetting = (key): boolean => {
        if (this.roleSettings.value) {
            if (this.roleSettings.value[key + '-show'] != null) {
                return this.roleSettings.value[key + '-show'];
            }
            return true;
        }
        return true;
    };

    enableSetting = (key): boolean => {
        if (this.roleSettings.value) {
            if (this.roleSettings.value[key + '-enable'] != null) {
                return this.roleSettings.value[key + '-enable'];
            }
            return true;
        }
        return true;
    };

    getReadOnlyModeInfo(): Observable<{isReadOnlyModeEnabled: boolean; customerGroupIdFilter: number | null}> {
        return this._principal.getAuthenticationState().pipe(
            map((loggedInUser: IUserEntity) => {
                let isReadOnlyModeEnabled: boolean = false;
                let customerGroupIdFilter: number | null = null;

                console.log(`Logged In User Roles: ${loggedInUser.roles}`);
                if (!Array.isArray(loggedInUser.roles)) {
                    throw new Error('Logged In User Roles is not an array');
                }

                const READ_ONLY_ROLE_MARKER = '_readonly';
                const CUSTOMER_GROUP_FILTER_ROLE_MARKER = '_cgrp_id_';

                const readOnlyRole: string | undefined = loggedInUser.roles
                    .map((role: string) => role.toLowerCase())
                    .find((role: string) => role.includes(READ_ONLY_ROLE_MARKER));
                console.log(`Read-only role: ${readOnlyRole}`);

                if (readOnlyRole != null) {
                    isReadOnlyModeEnabled = true;
                }

                for (const role of loggedInUser.roles as string[]) {
                    const customerGroupIdFilterRoleMarkerIdx: number = role.toLowerCase().indexOf(CUSTOMER_GROUP_FILTER_ROLE_MARKER);

                    if (customerGroupIdFilterRoleMarkerIdx !== -1) {
                        console.log(`Customer group ID filter role: ${role}`);

                        const customerGroupIdFilterStr: string = role.substring(
                            customerGroupIdFilterRoleMarkerIdx + CUSTOMER_GROUP_FILTER_ROLE_MARKER.length
                        );

                        if (isFinite(parseInt(customerGroupIdFilterStr, 10))) {
                            customerGroupIdFilter = parseInt(customerGroupIdFilterStr, 10);
                            break;
                        }
                    }
                }

                return {
                    isReadOnlyModeEnabled,
                    customerGroupIdFilter
                };
            })
        );
    }
}
